import { useContext, useEffect } from 'react';
import { UserContext } from '../contexts/UserContext';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { primeOfflineData, resetPrimeThrottle } from '../utils/offlinePrime';

// Headless: warms the offline cache in the background so the app has the reference
// data (classes, rosters, subjects, evaluation windows) needed to actually work
// offline — not just the pages the user happened to visit while connected.
export default function OfflinePrimer() {
  const user = useContext(UserContext);
  const online = useOnlineStatus();

  // Prime once the user is known, and again whenever connectivity returns.
  useEffect(() => {
    if (!user || !user.role || !online) return;

    // Defer so priming never competes with the first paint or the page's own loads.
    const id = setTimeout(() => {
      primeOfflineData(user);
    }, 4000);

    return () => clearTimeout(id);
  }, [user, online]);

  // Switching school, academic year or phase invalidates the working set that was
  // cached for the previous scope, so allow an immediate re-prime.
  useEffect(() => {
    const onScopeChange = () => {
      resetPrimeThrottle();
      if (user && user.role && online) primeOfflineData(user, { force: true });
    };
    window.addEventListener('app:phase:changed', onScopeChange);
    window.addEventListener('app:school:changed', onScopeChange);
    window.addEventListener('app:academicYear:changed', onScopeChange);
    return () => {
      window.removeEventListener('app:phase:changed', onScopeChange);
      window.removeEventListener('app:school:changed', onScopeChange);
      window.removeEventListener('app:academicYear:changed', onScopeChange);
    };
  }, [user, online]);

  // Re-prime periodically during long sessions so a teacher who has been logged in
  // all day still has current data if they lose signal in the afternoon.
  useEffect(() => {
    if (!user || !user.role) return;
    const id = setInterval(() => {
      if (navigator.onLine) primeOfflineData(user);
    }, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [user]);

  return null;
}
